import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Spinner } from "@nextui-org/react";
import { CheckCircle, XCircle } from "lucide-react";
import { toast } from "react-toastify";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../utils/firebase";
import { useAuth } from "../../context/AuthContext";

const PaymentVerification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [verificationStatus, setVerificationStatus] = useState("verifying");
  const [paymentDetails, setPaymentDetails] = useState(null);
  
  const searchParams = new URLSearchParams(location.search);
  const paymentId = searchParams.get("paymentId");
  const status = searchParams.get("status");
  
  useEffect(() => {
    const verifyPayment = async () => {
      try {
        if (!paymentId) {
          throw new Error("No payment ID provided");
        }
        
        // In a real app, you would verify with your backend
        // For demo, we'll just check Firestore
        const paymentRef = doc(db, "payments", paymentId);
        const paymentSnap = await getDoc(paymentRef);
        
        if (!paymentSnap.exists()) {
          throw new Error("Payment record not found");
        }
        
        const paymentData = paymentSnap.data();
        
        // Update payment status
        await updateDoc(paymentRef, {
          status: "verified",
          verifiedAt: new Date(),
        });
        
        setPaymentDetails(paymentData);
        setVerificationStatus("success");
        
        // Update user subscription if not already done
        if (user && user.uid) {
          const companyRef = doc(db, "companies", user.uid);
          await updateDoc(companyRef, {
            "subscription.status": "active",
          });
        }
        
      } catch (error) {
        console.error("Verification error:", error);
        setVerificationStatus("failed");
        toast.error(`Payment verification failed: ${error.message}`);
      }
    };
    
    if (status === "success") {
      verifyPayment();
    } else {
      setVerificationStatus("failed");
    }
  }, [paymentId, status, user]);
  
  const handleBackToDashboard = () => {
    navigate("/company/dashboard");
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-col items-center p-6">
          {verificationStatus === "verifying" && (
            <Spinner size="lg" className="mb-4" />
          )}
          {verificationStatus === "success" && (
            <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
          )}
          {verificationStatus === "failed" && (
            <XCircle className="w-16 h-16 text-red-500 mb-4" />
          )}
          <h2 className="text-2xl font-bold text-center">
            {verificationStatus === "verifying" && "Verifying Payment..."}
            {verificationStatus === "success" && "Payment Verified Successfully!"}
            {verificationStatus === "failed" && "Payment Verification Failed"}
          </h2>
        </CardHeader>
        <CardBody className="p-6">
          {verificationStatus === "verifying" && (
            <p className="text-center text-default-600">
              Please wait while we verify your payment...
            </p>
          )}
          
          {verificationStatus === "success" && paymentDetails && (
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-default-600">Plan:</span>
                <span className="font-medium">{paymentDetails.planName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-default-600">Amount:</span>
                <span className="font-medium">₹{paymentDetails.amount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-default-600">Payment ID:</span>
                <span className="font-medium">{paymentDetails.paymentId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-default-600">Status:</span>
                <span className="font-medium text-green-500">Verified</span>
              </div>
              
              <p className="mt-4 text-default-600">
                Your subscription has been activated. You can now post jobs according to your plan.
              </p>
            </div>
          )}
          
          {verificationStatus === "failed" && (
            <div className="space-y-4">
              <p className="text-center text-default-600">
                We couldn't verify your payment. Please contact support with your payment ID.
              </p>
              {paymentId && (
                <div className="bg-red-50 p-3 rounded">
                  <p className="text-red-600 text-sm">
                    Payment ID: {paymentId}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <Button
            color={verificationStatus === "success" ? "primary" : "default"}
            className="mt-6 w-full"
            onClick={handleBackToDashboard}
          >
            Back to Dashboard
          </Button>
        </CardBody>
      </Card>
    </div>
  );
};

export default PaymentVerification;